import * as THREE from 'three';
import SunCalc from 'suncalc';

export class LightingManager {
  public scene: THREE.Scene;
  public sunLight: THREE.DirectionalLight;
  public moonLight: THREE.DirectionalLight;
  public hemiLight: THREE.HemisphereLight;
  public ambientLight: THREE.AmbientLight;

  // Sky sphere / background colors
  public currentSkyColor: THREE.Color = new THREE.Color();
  public currentDeepColor: THREE.Color = new THREE.Color();
  public currentShallowColor: THREE.Color = new THREE.Color();

  private sunMesh: THREE.Mesh;
  private moonMesh: THREE.Mesh;

  // Coordinates for SunCalc
  private static readonly LAT = 24.1;
  private static readonly LON = 120.1;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Ambient light
    this.ambientLight = new THREE.AmbientLight(0x1e293b, 0.6);
    this.scene.add(this.ambientLight);

    // Hemisphere light (sky vs ground bounce)
    this.hemiLight = new THREE.HemisphereLight(0xbae6fd, 0x0f172a, 0.8);
    this.scene.add(this.hemiLight);

    // Sun directional light
    this.sunLight = new THREE.DirectionalLight(0xfffbeb, 2.0);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 2048;
    this.sunLight.shadow.mapSize.height = 2048;
    this.sunLight.shadow.camera.near = 10;
    this.sunLight.shadow.camera.far = 400;
    const shadowD = 120;
    this.sunLight.shadow.camera.left = -shadowD;
    this.sunLight.shadow.camera.right = shadowD;
    this.sunLight.shadow.camera.top = shadowD;
    this.sunLight.shadow.camera.bottom = -shadowD;
    this.sunLight.shadow.bias = -0.0005;
    this.scene.add(this.sunLight);

    // Moon directional light
    this.moonLight = new THREE.DirectionalLight(0x93c5fd, 0.6);
    this.scene.add(this.moonLight);

    // Visual Sun orb in sky
    const sunGeo = new THREE.SphereGeometry(6, 16, 16);
    const sunMat = new THREE.MeshBasicMaterial({ color: 0xffedd5 });
    this.sunMesh = new THREE.Mesh(sunGeo, sunMat);
    this.scene.add(this.sunMesh);

    // Visual Moon orb in sky
    const moonGeo = new THREE.SphereGeometry(4, 16, 16);
    const moonMat = new THREE.MeshBasicMaterial({ color: 0xe0f2fe });
    this.moonMesh = new THREE.Mesh(moonGeo, moonMat);
    this.scene.add(this.moonMesh);
  }

  public updateTime(timeOfDayHours: number, useSunCalc: boolean = false, date: Date = new Date()) {
    let sunAltitudeRad: number;
    let sunAzimuthRad: number;
    let moonAltitudeRad: number;
    let moonAzimuthRad: number;

    if (useSunCalc) {
      // Calculate using actual SunCalc library with Taiwan Strait coordinates
      // Set hours on target date
      const calcDate = new Date(date);
      const hours = Math.floor(timeOfDayHours);
      const minutes = Math.floor((timeOfDayHours - hours) * 60);
      calcDate.setHours(hours, minutes, 0, 0);

      const sunPos = SunCalc.getPosition(calcDate, LightingManager.LAT, LightingManager.LON);
      sunAltitudeRad = sunPos.altitude;
      sunAzimuthRad = sunPos.azimuth;

      const moonPos = SunCalc.getMoonPosition(calcDate, LightingManager.LAT, LightingManager.LON);
      moonAltitudeRad = moonPos.altitude;
      moonAzimuthRad = moonPos.azimuth;
    } else {
      // Schematic mode (Educational 24h continuous curve)
      // Noon is hour 12 (highest sun), sunrise ~6, sunset ~18
      const hourAngle = ((timeOfDayHours - 12) / 24) * Math.PI * 2;
      sunAltitudeRad = Math.sin(-hourAngle + Math.PI / 2) * (Math.PI / 2.8);
      sunAzimuthRad = hourAngle;

      // Moon opposite to sun
      moonAltitudeRad = -sunAltitudeRad;
      moonAzimuthRad = sunAzimuthRad + Math.PI;
    }

    const distance = 240;

    // Convert spherical sun coords to 3D Three.js coords
    // Y is up, X is East-West, Z is North-South
    const sunH = Math.cos(sunAltitudeRad);
    const sunY = Math.sin(sunAltitudeRad) * distance;
    const sunX = Math.sin(sunAzimuthRad) * distance * sunH;
    const sunZ = -Math.cos(sunAzimuthRad) * distance * sunH;

    this.sunLight.position.set(sunX, Math.max(sunY, -30), sunZ);
    this.sunMesh.position.set(sunX, sunY, sunZ);
    this.sunMesh.visible = sunY > -10;

    // Convert moon coords
    const moonH = Math.cos(moonAltitudeRad);
    const moonY = Math.sin(moonAltitudeRad) * distance;
    const moonX = Math.sin(moonAzimuthRad) * distance * moonH;
    const moonZ = -Math.cos(moonAzimuthRad) * distance * moonH;

    this.moonLight.position.set(moonX, Math.max(moonY, -30), moonZ);
    this.moonMesh.position.set(moonX, moonY, moonZ);
    this.moonMesh.visible = moonY > -10;

    // Calculate time colors & lighting intensities
    const sunElevation = Math.max(Math.sin(sunAltitudeRad), -0.2);
    
    // Day progress factor [-1 (deep night) -> 0 (twilight) -> 1 (noon)]
    const dayFactor = Math.sin(sunAltitudeRad);

    if (dayFactor > 0.15) {
      // Daytime (10:00 - 16:00)
      this.currentSkyColor.set('#60a5fa'); // Vibrant sky blue
      this.currentDeepColor.set('#0b2a4a');
      this.currentShallowColor.set('#0d738f');
      this.sunLight.color.set(0xfff7ed);
      this.sunLight.intensity = THREE.MathUtils.lerp(1.2, 2.4, (dayFactor - 0.15) / 0.85);
      this.moonLight.intensity = 0.05;
      this.ambientLight.intensity = 0.55;
      this.hemiLight.color.set(0xbae6fd);
      this.hemiLight.groundColor.set(0x1e293b);
      this.hemiLight.intensity = 0.7;
    } else if (dayFactor > -0.05) {
      // Golden Hour & Sunset/Sunrise
      const sunsetT = (dayFactor - (-0.05)) / 0.2; // 0 to 1
      this.currentSkyColor.set('#f97316').lerp(new THREE.Color('#38bdf8'), sunsetT);
      this.currentDeepColor.set('#1e1b4b').lerp(new THREE.Color('#0b2a4a'), sunsetT);
      this.currentShallowColor.set('#c2410c').lerp(new THREE.Color('#0d738f'), sunsetT);
      this.sunLight.color.set(0xfb923c); // Warm amber
      this.sunLight.intensity = THREE.MathUtils.lerp(0.4, 1.2, sunsetT);
      this.moonLight.intensity = 0.1;
      this.ambientLight.intensity = 0.45;
      this.hemiLight.color.set(0xfdba74);
      this.hemiLight.groundColor.set(0x431407);
      this.hemiLight.intensity = 0.55;
    } else {
      // Nighttime
      const nightDepth = Math.min(Math.abs(dayFactor), 1.0);
      this.currentSkyColor.set('#030712').lerp(new THREE.Color('#0f172a'), 0.5);
      this.currentDeepColor.set('#020617');
      this.currentShallowColor.set('#0f2b46');
      this.sunLight.intensity = 0.0;
      this.moonLight.color.set(0x93c5fd);
      this.moonLight.intensity = 0.75;
      // Keep clear visibility at night so wind turbine and UI remain fully observable
      this.ambientLight.color.set(0x38bdf8);
      this.ambientLight.intensity = 0.35;
      this.hemiLight.color.set(0x38bdf8);
      this.hemiLight.groundColor.set(0x020617);
      this.hemiLight.intensity = 0.4;
    }

    this.scene.background = this.currentSkyColor;
    if (this.scene.fog) {
      this.scene.fog.color.copy(this.currentSkyColor);
    }
  }
}
